import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiSearch, FiChevronDown, FiChevronRight, FiFilter } from 'react-icons/fi';
import { FaTractor } from 'react-icons/fa';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';

export interface SelectableItem {
  id: string;
  name: string;
  subtitle?: string;
  badge?: string;
  badgeColor?: 'green' | 'blue' | 'red' | 'gray' | 'yellow';
  icon?: React.ReactNode;
  metadata?: any;
  isActive?: boolean;
  children?: SelectableItem[];
}

interface AccountSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: SelectableItem) => void;
  items: SelectableItem[];
  selectedItem?: SelectableItem | null;
  title: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  showClearSelection?: boolean;
  allowGroupExpansion?: boolean;
  showActiveFilter?: boolean;
  customFilters?: {
    label: string;
    options: { label: string; value: string }[];
    onChange: (value: string) => void;
    value: string;
  }[];
}

const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  items,
  selectedItem,
  title,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found',
  showClearSelection = true,
  allowGroupExpansion = true,
  showActiveFilter = true,
  customFilters = []
}) => {
  const { t } = useTranslation('common');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;

  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setCurrentPage(1);
    const next = new Set<string>();
    if (selectedItem && allowGroupExpansion) {
      items.forEach((item) => {
        if (item.children?.some((child) => child.id === selectedItem.id)) {
          next.add(item.id);
        }
      });
    }
    setExpandedGroups(next);
  }, [isOpen, selectedItem, items, allowGroupExpansion]);

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const matchesQuery = (item: SelectableItem, search: string) =>
    item.name.toLowerCase().includes(search) ||
    item.subtitle?.toLowerCase().includes(search);

  const filterItems = (list: SelectableItem[]): SelectableItem[] => {
    let filtered = list;
    const search = searchTerm.trim().toLowerCase();

    if (search) {
      filtered = filtered
        .filter((item) => {
          const parentMatch = matchesQuery(item, search);
          const childMatch = item.children?.some((child) => matchesQuery(child, search));
          return parentMatch || childMatch;
        })
        .map((item) => {
          const parentMatch = matchesQuery(item, search);
          if (parentMatch || !item.children) return item;
          return {
            ...item,
            children: item.children.filter((child) => matchesQuery(child, search)),
          };
        });
    }

    if (showActiveFilter && activeFilter !== 'all') {
      const wantActive = activeFilter === 'active';
      filtered = filtered
        .map((item) => {
          if (!item.children?.length) return item;
          return {
            ...item,
            children: item.children.filter((child) => child.isActive === wantActive),
          };
        })
        .filter((item) => {
          if (item.children && item.children.length > 0) return true;
          if (item.children) return false;
          return item.isActive === wantActive;
        });
    }

    return filtered;
  };

  const filteredItems = filterItems(items);
  const search = searchTerm.trim();

  const childMatchesSearch = (child: SelectableItem) => {
    if (!search) return true;
    return matchesQuery(child, search.toLowerCase());
  };

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const pageItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const visibleRows: Array<{ item: SelectableItem; isChild: boolean }> = [];
  pageItems.forEach((item) => {
    visibleRows.push({ item, isChild: false });
    if (!item.children?.length) return;
    const showChildren =
      expandedGroups.has(item.id) ||
      (search.length > 0 && item.children.some(childMatchesSearch));
    if (!showChildren) return;
    const children = search ? item.children.filter(childMatchesSearch) : item.children;
    children.forEach((child) => visibleRows.push({ item: child, isChild: true }));
  });

  const handleSelect = (item: SelectableItem) => {
    onSelect(item);
    onClose();
  };

  const getBadgeColor = (color?: string) => {
    switch (color) {
      case 'green':
        return 'bg-emerald-50 text-emerald-800';
      case 'blue':
        return 'bg-sky-50 text-sky-800';
      case 'red':
        return 'bg-rose-50 text-rose-800';
      case 'yellow':
        return 'bg-amber-50 text-amber-800';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div className="fixed inset-0 z-[9999] bg-black/40" onClick={onClose} />

      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3">
        <div
          className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
            <h2 className="truncate text-base font-semibold text-slate-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            >
              <FiX className="size-4" />
            </button>
          </div>

          <div className="space-y-2 border-b border-slate-100 px-3 py-2.5">
            <label className="relative block">
              <FiSearch className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="h-8 w-full rounded-lg border border-slate-200 bg-white py-1 ps-8 pe-8 text-sm outline-none focus:border-emerald-400"
                autoFocus
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-700"
                >
                  <FiX className="size-3.5" />
                </button>
              )}
            </label>

            {(showActiveFilter || customFilters.length > 0) && (
              <div className="flex flex-wrap items-center gap-2">
                {showActiveFilter && (
                  <div className="flex items-center gap-1.5">
                    <FiFilter className="size-3.5 text-slate-400" />
                    <select
                      value={activeFilter}
                      onChange={(e) => {
                        setActiveFilter(e.target.value as 'all' | 'active' | 'inactive');
                        setCurrentPage(1);
                      }}
                      className="h-7 rounded-lg border border-slate-200 px-2 py-0.5 text-xs outline-none focus:border-emerald-400"
                    >
                      <option value="all">{t('selection.allItems')}</option>
                      <option value="active">{t('selection.activeOnly')}</option>
                      <option value="inactive">{t('selection.inactiveOnly')}</option>
                    </select>
                  </div>
                )}

                {customFilters.map((filter, idx) => (
                  <div key={idx} className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-500">{filter.label}</span>
                    <select
                      value={filter.value}
                      onChange={(e) => {
                        filter.onChange(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="h-7 rounded-lg border border-slate-200 px-2 py-0.5 text-xs outline-none focus:border-emerald-400"
                    >
                      {filter.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs text-slate-500">
              {t('selection.showing')} {filteredItems.length}{' '}
              {filteredItems.length === 1 ? t('selection.result') : t('selection.results')}
            </p>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {showClearSelection && selectedItem && (
              <button
                type="button"
                onClick={() => {
                  onSelect(null as any);
                  onClose();
                }}
                className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-start text-xs text-slate-600 hover:bg-slate-50"
              >
                <FiX className="size-3.5" />
                <span className="font-medium">{t('selection.clearSelection')}</span>
              </button>
            )}

            {visibleRows.length > 0 ? (
              <ul>
                {visibleRows.map(({ item, isChild }) => {
                  const isSelected = selectedItem?.id === item.id;
                  const hasChildren = Boolean(item.children?.length);
                  const isExpanded =
                    expandedGroups.has(item.id) ||
                    (search.length > 0 && hasChildren);
                  const expandable = hasChildren && allowGroupExpansion;

                  if (isChild) {
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(item)}
                          className={`flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2 ps-9 text-start hover:bg-emerald-50/70 ${
                            isSelected ? 'bg-emerald-50 ring-inset ring-1 ring-emerald-200' : ''
                          }`}
                        >
                          {item.icon || <FaTractor className="size-4 shrink-0 text-emerald-600" />}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900">
                              {item.name}
                            </span>
                            {item.subtitle && (
                              <span className="block truncate text-xs text-slate-500">
                                {item.subtitle}
                              </span>
                            )}
                          </span>
                          {item.badge && (
                            <span
                              className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${getBadgeColor(item.badgeColor)}`}
                            >
                              {item.badge}
                            </span>
                          )}
                          {isSelected && (
                            <span className="shrink-0 text-[11px] font-medium text-emerald-700">
                              {t('selection.selected')}
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  }

                  return (
                    <li
                      key={item.id}
                      className={`border-b border-slate-100 ${
                        isSelected ? 'bg-sky-50' : ''
                      }`}
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            expandable ? toggleGroupExpansion(item.id) : handleSelect(item)
                          }
                          className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-start hover:bg-slate-50"
                        >
                          {expandable ? (
                            isExpanded ? (
                              <FiChevronDown className="size-3.5 shrink-0 text-slate-500" />
                            ) : (
                              <FiChevronRight className="size-3.5 shrink-0 text-slate-500 rtl:rotate-180" />
                            )
                          ) : (
                            item.icon || (
                              <HiOutlineOfficeBuilding className="size-4 shrink-0 text-sky-600" />
                            )
                          )}
                          {expandable &&
                            (item.icon || (
                              <HiOutlineOfficeBuilding className="size-4 shrink-0 text-sky-600" />
                            ))}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-900">
                              {item.name}
                            </span>
                            {item.subtitle && (
                              <span className="block truncate text-xs text-slate-500">
                                {item.subtitle}
                              </span>
                            )}
                          </span>
                          {item.badge && (
                            <span
                              className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${getBadgeColor(item.badgeColor)}`}
                            >
                              {item.badge}
                            </span>
                          )}
                          {isSelected && (
                            <span className="shrink-0 text-[11px] font-medium text-sky-700">
                              {t('selection.selected')}
                            </span>
                          )}
                        </button>
                        {expandable && (
                          <button
                            type="button"
                            onClick={() => handleSelect(item)}
                            className="me-2 shrink-0 rounded-lg px-2 py-0.5 text-xs text-sky-800 ring-1 ring-sky-100 hover:bg-sky-50"
                          >
                            {t('selection.viewOrg')}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="px-3 py-10 text-center text-sm text-slate-500">
                {searchTerm ? (
                  <div>
                    <p className="mb-2">
                      {t('selection.noResultsFoundFor')} “{searchTerm}”
                    </p>
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="text-sm font-medium text-emerald-700 hover:text-emerald-800"
                    >
                      {t('selection.clearSearch')}
                    </button>
                  </div>
                ) : (
                  <p>{emptyMessage}</p>
                )}
              </div>
            )}
          </div>

          {filteredItems.length > itemsPerPage && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-3 py-2">
              <p className="text-xs text-slate-500">
                {t('selection.page')} {currentPage} {t('selection.of')} {totalPages}
              </p>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="rounded-lg px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t('buttons.previous')}
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-lg px-2 py-0.5 text-xs text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-40"
                >
                  {t('buttons.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default AccountSelectionModal;
